import _defineProperty from "@babel/runtime/helpers/defineProperty";
/*
 *                     NOTICE
 *            COPYRIGHT 2025 Ab Initio
 *       UNPUBLISHED - ALL RIGHTS RESERVED
 *
 * This program is confidential and a trade secret of Ab Initio.
 * The receipt or possession of this program does not convey any
 * rights to reproduce or disclose its contents, or to manufacture,
 * use or sell anything that it may describe, in whole or in part,
 * without the specific written consent of Ab Initio.
 */

import { createPageStorageIdentity, createPageStorageIdentityFromKey } from "abreact/core/pagestate/IPageStateStorageIdentity";
import { PageInstanceStateStorage } from "abreact/core/pagestate/PageInstanceStateStorage";
import { PageStateLogger } from "abreact/core/pagestate/PageStateLogger";
import { StorageCategory } from "abreact/core/pagestate/StorageCategory";
import { AbSessionStorage } from "abweb/utils/AbSessionStorage";
export class StandardPageStateStorageService {
  constructor(maxStorage) {
    _defineProperty(this, "_maxStorage", void 0);
    _defineProperty(this, "pageInstanceStateStorageByCategoryAndIdentityKeyMap", new Map());
    _defineProperty(this, "forgottenStorage", []);
    this._maxStorage = maxStorage;
  }
  establishNewPageInstanceStateStorage(category) {
    const storage = new PageInstanceStateStorage(createPageStorageIdentity(category, this.getNextRID()));
    let storageMapToUse = this.getPageInstanceStorageMapForCategory(category);
    storageMapToUse.set(storage.getStorageIdentity().key, storage);
    PageStateLogger.maybeLog("system", `established new page state storage ${storage.getStorageIdentity().key}`);
    this.removeForgottenStorage();
    return storage;
  }
  removeForgottenStorage() {
    if (this.forgottenStorage.length > 0) {
      this.forgottenStorage.forEach(storage => {
        this.getPageInstanceStorageMapForCategory(StorageCategory.PRIMARY).delete(storage.getStorageIdentity().key);
        this.getPageInstanceStorageMapForCategory(StorageCategory.SECONDARY).delete(storage.getStorageIdentity().key);
        PageStateLogger.maybeLog("system", `forgetting state storage with key ${storage.getStorageIdentity().key}`);
      });
      this.forgottenStorage = [];
    }
  }
  getPageInstanceStateStorage(storageIdentity) {
    const storageMapTouse = this.getPageInstanceStorageMapForCategory(storageIdentity.category);
    let storage = storageMapTouse.get(storageIdentity.key);
    if (storage == null) {
      storage = this.readStateStorageFromSession(storageIdentity);
      if (storage) {
        storageMapTouse.set(storageIdentity.key, storage);
      }
    }
    return storage;
  }
  storePageInstanceStateStorage(pageStateStorage) {
    if (pageStateStorage) {
      this.writeToStateStorageToSession(pageStateStorage);
    }
  }
  forgetPageStateStorage(pageStateStorage) {
    if (pageStateStorage) {
      this.forgottenStorage.push(pageStateStorage);
      PageStateLogger.maybeLog("system", `marking state storage ${pageStateStorage.getStorageIdentity().key} to be forgotten`);
    }
  }
  movePageInstanceStateStorage(fromIdentity, toStorageCategory) {
    const fromPageStateStorage = this.getPageInstanceStateStorage(fromIdentity);
    const toIdentity = createPageStorageIdentity(toStorageCategory, this.getNextRID());
    const toPageStateStorage = new PageInstanceStateStorage(toIdentity);
    if (fromPageStateStorage) {
      toPageStateStorage.restoreCapturedState(fromPageStateStorage.convertStateStorageToJsonObjects());
      // don't leave this hanging around creating bloat. It's no longer needed here.
      this.forgetPageStateStorage(fromPageStateStorage);
      PageStateLogger.maybeLog("system", `moved state storage from ${fromIdentity.key} to ${toPageStateStorage.getStorageIdentity().key}`);
    }
    this.writeToStateStorageToSession(toPageStateStorage);
    return toIdentity;
  }
  getOrCreateStorageForCategory(storageCategory, contextName, currentRid, currentPageStateStorageIdentity) {
    let pageStateStorage = undefined;

    // Will only be a rid extra param if the url was previously visited (browser back/forward buttons),
    // or external bookmark.  Even then lookup can fail due to LRU eviction or bookmark from previous login session

    let stateStorageKey;
    if (storageCategory === StorageCategory.PRIMARY) {
      stateStorageKey = currentRid;
    } else {
      // If a secondary route is rendered WITHIN a primary route, then it's storage is the same storage as teh
      // primary route (so it will be restored along with the primary route component state)
      if (currentPageStateStorageIdentity?.category === StorageCategory.PRIMARY) {
        storageCategory = StorageCategory.PRIMARY;
        stateStorageKey = currentPageStateStorageIdentity.key;
      }
    }
    if (stateStorageKey) {
      /*
        If the storage category of the current route is PRIMARY yet the storageKey (rid) assigned belongs to the SECONDARY
        category, this the storage for the existing rid needs to be promoted to the PRIMARY category (this is a the case
        where the user has "promoted" a side-panel secondary route to the main window)
       */
      if (storageCategory === StorageCategory.PRIMARY) {
        if (stateStorageKey.startsWith(StorageCategory.SECONDARY)) {
          const toIdentity = this.movePageInstanceStateStorage(createPageStorageIdentityFromKey(stateStorageKey), StorageCategory.PRIMARY);
          stateStorageKey = toIdentity.key;
        }
      }
      pageStateStorage = this.getPageInstanceStateStorage(createPageStorageIdentityFromKey(stateStorageKey));
      if (pageStateStorage) {
        PageStateLogger.maybeLog("system", `reloaded state storage with key ${pageStateStorage.getStorageIdentity().key}`);
        return pageStateStorage;
      }
      /*
      For a secondary route, there will always be a RID (the route name), but on the first render, there will be no existing
      page state storage. Don't log anything in that case. If there is a rid and this is a primary route, the only reason that
      there would not be state storage available is that 1) the user reloaded the browser so in-memory storage was cleared and 2)
      it rolled off the set of state storage held in sessionStorage
      */
      if (storageCategory === StorageCategory.PRIMARY) {
        PageStateLogger.logWarning(`No PageStateStorage instance can be retrieved for routeRenderContext="${contextName}" and rid="${stateStorageKey}". The page will render as a new instance.`);
      }
    }

    // create new page state storage with new rid for primary and using the route name for secondary
    const storage = this.establishNewPageInstanceStateStorage(storageCategory);
    return storage;
  }

  // millis since 1970, no intersection between different user sessions
  getNextRID() {
    return String(StandardPageStateStorageService.RID_ID++); // e.g., "1734103787893"
  }
  getPageInstanceStorageMapForCategory(type) {
    let storageMapToUse = this.pageInstanceStateStorageByCategoryAndIdentityKeyMap.get(type);
    if (!storageMapToUse) {
      storageMapToUse = new Map();
      this.pageInstanceStateStorageByCategoryAndIdentityKeyMap.set(type, storageMapToUse);
    }
    return storageMapToUse;
  }
  writeToStateStorageToSession(storage) {
    const category = storage.getStorageIdentity().category;
    const keys = this.getOrCreateActiveKeys(category);
    let isExistingKey = keys.indexOf(storage.getStorageIdentity().key) > -1;
    if (!isExistingKey) {
      keys.push(storage.getStorageIdentity().key);
      this.storeActiveKeys(category, keys);
      this.rollOffIfNecessary(category);
    }
    const jsonValue = this.generateJsonForStorage(storage);
    if (jsonValue) {
      AbSessionStorage.setItem(storage.getStorageIdentity().key, jsonValue);
      PageStateLogger.maybeLog("system", `wrote page state storage ${storage.getStorageIdentity().key}`);
    } else {
      AbSessionStorage.removeItem(storage.getStorageIdentity().key);
    }
  }
  generateJsonForStorage(storage) {
    let stringContent;
    try {
      stringContent = JSON.stringify(storage.convertStateStorageToJsonObjects(), null, 2);
    } catch (e) {
      PageStateLogger.logError(`Unable to create storage content for storage ${storage.getStorageIdentity().key}: ${e.message}`);
    }
    return stringContent ?? "";
  }
  readStateStorageFromSession(storageIdentity) {
    let storage;
    const storageObjects = this.parseSavedStateStorage(storageIdentity);
    if (storageObjects) {
      storage = new PageInstanceStateStorage(storageIdentity);
      storage.restoreCapturedState(storageObjects);
      PageStateLogger.maybeLog("system", `restored state storage from session for key ${storageIdentity.key}`);
    }
    return storage;
  }
  parseSavedStateStorage(storageIdentity) {
    const storageValue = AbSessionStorage.getItem(storageIdentity.key);
    let storageObjects;
    if (storageValue) {
      try {
        storageObjects = JSON.parse(storageValue);
      } catch (e) {
        PageStateLogger.logError(`Unable to read stored session value for ${storageIdentity.key}: ${e.message}`);
      }
    }
    return storageObjects;
  }
  createActiveKeysItemNameStorageCategory(category) {
    return `ACTIVE_KEYS:${category}`;
  }
  getOrCreateActiveKeys(category) {
    let keys = null;
    const stringRepresentation = AbSessionStorage.getItem(this.createActiveKeysItemNameStorageCategory(category));
    if (stringRepresentation != null) {
      try {
        keys = JSON.parse(stringRepresentation);
      } catch (e) {
        PageStateLogger.logError(`Unable to parse session storage content as string[] for key ${this.createActiveKeysItemNameStorageCategory(category)}: ${e.message}`);
      }
    }
    if (keys === null) {
      keys = [];
    }
    return keys;
  }
  storeActiveKeys(category, keys) {
    AbSessionStorage.setItem(this.createActiveKeysItemNameStorageCategory(category), JSON.stringify(keys));
  }
  rollOffIfNecessary(category) {
    const keys = this.getOrCreateActiveKeys(category);
    if (keys.length > this._maxStorage) {
      const keyOfStorageToRemove = keys[0];
      const newActiveKeys = keys.splice(1);
      AbSessionStorage.removeItem(keyOfStorageToRemove);
      this.getPageInstanceStorageMapForCategory(category).delete(keyOfStorageToRemove);
      this.storeActiveKeys(category, newActiveKeys);
      PageStateLogger.maybeLog("system", `rolled off active key ${keyOfStorageToRemove}`);
    }
  }
}
_defineProperty(StandardPageStateStorageService, "RID_ID", new Date().getTime());
//# sourceMappingURL=StandardPageStateStorageService.js.map
